import React, { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Typography, } from '@mui/material';
import { WhiteBox } from 'components/AppShell';
import { useNavigateWithClient } from 'hooks';
import CustomButton from 'components/CustomButton';
import NoAccessTooltip from 'components/NoAccessTooltip';
import { useSelectedBatchRemoved } from 'announcementStore';

const useStyles = makeStyles((theme) => ({
  carouselContainer: {
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
    minHeight: '300px',
  },
  banner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
  },
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4)
  },
  bulletPoint: {
    display: 'flex',
  },
  bulletIcon: {
    width: '6px',
    height: '6px',
    backgroundColor: theme.palette.neutral['clr-700'],
    borderRadius: '50%',
    marginRight: '12px',
    marginTop: '6px',
    flexShrink: 0,
  },
  visualSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    maxWidth: '45%',
  },
  resumePreview: {
    width: '120px',
    height: '150px',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  resumeLine: {
    height: '4px',
    backgroundColor: '#e0e0e0',
    borderRadius: '2px',
    '&.header': {
      height: '8px',
      backgroundColor: '#2196f3',
      width: '80%',
    },
    '&.medium': {
      width: '90%',
    },
    '&.short': {
      width: '60%',
    },
  },
  avatarContainer: {
    position: 'absolute',
    left: '-20px',
    top: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  avatar: {
    width: '40px',
    height: '40px',
    border: '3px solid white',
  },
  matchBadge: {
    position: 'absolute',
    bottom: '-10px',
    right: '-10px',
    backgroundColor: '#4caf50',
    color: 'white',
    borderRadius: '50%',
    width: '50px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: 'bold',
    border: '3px solid white',
    textAlign: 'center',
    lineHeight: '1.1',
  },
  indicatorContainer: {
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '8px',
  },
  indicator: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#D9D9D9',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&.active': {
      backgroundColor: '#1961BF',
      transform: 'scale(1.2)',
    },
  },
}));

const bannerData = [
  {
    title: "Fine-tune your resume to perfectly match the job description",
    bullets: [
      "Use the job description to improve what matters most",
      "Get clear, targeted suggestions that help you stand out"
    ],
    buttonText: "Check JD & Resume Relevance",
    className: "banner-1",
    showAvatars: false,
    image: 'https://assets.languify.in/images/jdResumeShort.png'
  },
  {
    title: "Make your resume speak for the job role you want",
    bullets: [
      "Ensure your resume is suitable for the role you're applying for",
      "Highlight relevant experiences in ways that align with the role"
    ],
    buttonText: "Instant Review",
    className: "banner-2",
    showAvatars: true,
    image: 'https://assets.languify.in/images/jrResumeShort.png'
  },
  {
    title: "Get your relevance score, fine-tune your resume if needed, and share it for the shortlisting task",
    bullets: [
      "Make sure your resume aligns before it's reviewed for shortlisting",
      "The better your alignment, the higher your chances of getting shortlisted"
    ],
    className: "banner-3",
    showAvatars: false,
    image: 'https://assets.languify.in/images/jdJrResumeShort.png'
  }
];

const ShortlistingBanner = () => {
  const classes = useStyles();
  const navigateWithClient = useNavigateWithClient()
  const removed = useSelectedBatchRemoved();

  const [currentBanner, setCurrentBanner] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % bannerData.length);
    }, 8000);

    return () => clearInterval(timer);
  }, []);

  const handleIndicatorClick = (index) => {
    setCurrentBanner(index);
  };

  const currentData = bannerData[currentBanner];

  const handleReview = () => {
    if (currentData.className === 'banner-2') {
      navigateWithClient('/shortlisting-genie/only-resume')
    } else if (
      currentData.className === 'banner-1'
    ) {
      navigateWithClient('/shortlisting-genie/jd-plus-resume')
    }
  }

  return (
    <WhiteBox sx={{ position: 'relative' }}>
      <img
        src='https://assets.languify.in/images/gradientBlue.png'
        alt='texture'
        width={300}
        height='100%'
        style={{
          position: 'absolute',
          left: 0,
          top: 0
        }}
      />
      <Box className={`${classes.banner} ${currentData.className}`}>
        <Box className={classes.contentSection}>
          <Typography variant='h5-semiBold' color='neutral.clr-900'>
            {currentData.title}
          </Typography>

          {currentData.bullets.map((bullet, index) => (
            <Box key={index} className={classes.bulletPoint}>
              <Box className={classes.bulletIcon} />
              <Typography variant='body01-bold' color='neutral.clr-700'>
                {bullet}
              </Typography>
            </Box>
          ))}

          <Box sx={{ display: 'flex', gap: 2, marginTop: '8px' }}>
            <NoAccessTooltip show={removed}>
              <CustomButton
                onClick={() => navigateWithClient('/shortlisting-genie/jd-plus-resume')}
                size='medium'
                sx={{ width: 'fit-content' }}
                disabled={removed}
              >
                Check JD & Resume Relevance
              </CustomButton>
            </NoAccessTooltip>
            <NoAccessTooltip show={removed}>
              <CustomButton
                onClick={() => navigateWithClient('/shortlisting-genie/only-resume')}
                size='medium'
                variant='outlined'
                sx={{ width: 'fit-content' }}
                disabled={removed}
              >
                Instant Review
              </CustomButton>
            </NoAccessTooltip>
          </Box>
        </Box>
        <img
          src={currentData.image}
          alt='banner'
          width={300}
          height={300}
        />
      </Box>

      <Box className={classes.indicatorContainer}>
        {bannerData.map((_, index) => (
          <Box
            key={index}
            className={`${classes.indicator} ${currentBanner === index ? 'active' : ''}`}
            onClick={() => handleIndicatorClick(index)}
          />
        ))}
      </Box>
    </WhiteBox>
  );
};

export default ShortlistingBanner;